// src/pages/Cloud/components/fsc/fscEditClauseUtils.js
// 編輯條款用：樹狀節點搜尋、路徑文字、狀態顯示

/** 合併樹／表格節點（含 FSCComplianceDetailPanel 映射後的子節點） */
export function isFscControlNode(control) {
  return String(control?.standard_key || '').toLowerCase() === 'fsc';
}

/** 從頂層 controls 取 FSC 根（略過 CIS 等）；先 standard_key，再 fallback control_id */
export function findFscRootInControls(controls) {
  if (!Array.isArray(controls)) return null;
  return (
    controls.find(isFscControlNode) ||
    controls.find((n) => String(n?.control_id || '').toUpperCase() === 'FSC') ||
    null
  );
}

/** 若節點缺 id，以 uuid 補上，供 buildNodeIndex／側欄路徑一致使用 */
export function normalizeTreeEnsureId(nodes) {
  const walk = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((n) => {
      const children = Array.isArray(n?.children) ? n.children : [];
      const hasId = n?.id !== undefined && n?.id !== null;
      const fallbackId = n?.uuid ?? null;

      return {
        ...n,
        id: hasId ? n.id : fallbackId,
        children: walk(children),
      };
    });
  };
  return walk(nodes);
}

export function getNodePk(node) {
  const raw = node?.id ?? node?.uuid ?? node?.pk ?? null;
  if (raw === undefined || raw === null) return null;
  const s = String(raw).trim();
  return s || null;
}

export function buildNodeIndex(tree = []) {
  const byPk = new Map();

  const walk = (nodes, parentPk = null, path = []) => {
    (nodes || []).forEach((n) => {
      const pk = getNodePk(n);
      if (!pk) return;

      const nextPath = [...path, n];
      byPk.set(pk, { node: n, parentPk, path: nextPath });

      const children = Array.isArray(n.children) ? n.children : [];
      if (children.length > 0) walk(children, pk, nextPath);
    });
  };

  walk(tree, null, []);
  return { byPk };
}

// 統一處理顯示格式："{control_id} {control_name}"
export function formatControlOptionLabel(node) {
  const cid = String(node?.control_id || '').trim();
  const name = String(node?.control_name || '').trim();

  if (cid && name) return `${cid} ${name}`;
  if (name) return name;
  if (cid) return cid;
  return '';
}

// 草稿清單路徑：顯示 control_id + control_name
export function getPathLabel(pathNodes = []) {
  const parts = (pathNodes || [])
    .map((n) => formatControlOptionLabel(n))
    .map((s) => String(s || '').trim())
    .filter(Boolean);

  return parts.length > 0 ? parts.join(' / ') : '(未選擇階層)';
}

export function statusLabel(status) {
  if (status === 'passed') return '通過';
  if (status === 'partial') return '部分通過';
  if (status === 'failed') return '未通過';
  return '未設定';
}

export function getNodeChildrenOptions(node) {
  const children = Array.isArray(node?.children) ? node.children : [];
  return children
    .map((c) => {
      const pk = getNodePk(c);
      return {
        pk,
        label: formatControlOptionLabel(c),
        node: c,
      };
    })
    .filter((o) => Boolean(o.pk));
}

/**
 * 根據 leaf pk 回推出「逐層下拉」所需的 selectedPks
 * @returns number[] 例如 [level1pk, level2pk, level3pk]
 */
export function getSelectedPathPksFromLeaf(index, leafPk) {
  const record = index?.byPk?.get(String(leafPk));
  if (!record?.path) return [];
  return record.path
    .map((n) => getNodePk(n))
    .filter(Boolean);
}
